import { useEffect, useState } from 'react';
import {
  useGenerateSubmissionsMutation,
  useSubmissionsListQuery,
} from '@common/cache';
import dayjs, { Dayjs } from 'dayjs';
import { SelectChangeEvent } from '@mui/material';

import { SubmissionColumnNames } from '@views/pages/DataAnalysis/components/Submissions/components';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

export interface ISubmissionTableEntries {
  id: string;
  aligned_patients: number;
  download_date: string;
  date: {
    from: string;
    to: string;
  };
  organization_name: string;
  patients: number;
  pending_patients: number;
  program_name: string;
  rejected_patients: number;
}

export interface ISubmissionTableFilters {
  sort_by: SubmissionColumnNames;
  sort_order: 'asc' | 'desc';
  page: number;
  limit: number;
  from: Dayjs;
  to: Dayjs;
}

const defaultFromDate = new Date();
defaultFromDate.setMonth(defaultFromDate.getMonth() - 1);

const initialFilter: ISubmissionTableFilters = {
  sort_order: 'desc',
  sort_by: 'date',
  page: 1,
  limit: 100,
  from: dayjs(defaultFromDate),
  to: dayjs(new Date()),
};

export const useSubmissions = ({
  programId,
  orgId,
  setOrgId,
  setProgramId,
}: {
  programId: string;
  orgId: string;
  setOrgId: (orgId: string) => void;
  setProgramId: (programId: string) => void;
}) => {
  const [filters, setFilters] =
    useState<ISubmissionTableFilters>(initialFilter);

  const {
    data = {
      count: 0,
      submissions: [],
      patients_ready_to_submit: 0,
    },
    refetch,
    isFetching,
  } = useSubmissionsListQuery(programId, orgId, filters);

  const entries: ISubmissionTableEntries[] = data.submissions.map(
    submission => ({
      download_date: submission.download_date
        ? dayjs(submission.download_date).format('MMM DD,YYYY')
        : '-',
      id: submission.id,
      organization_name: submission.organization_name,
      program_name: submission.program_name,
      date: {
        from: submission.earliest_date,
        to: submission.latest_date,
      },
      patients: submission.patients,
      pending_patients: submission.pending_patients,
      aligned_patients: submission.aligned_patients,
      rejected_patients: submission.rejected_patients,
    }),
  );

  useEffect(() => {
    if (programId && orgId) {
      refetch();
    }
  }, [programId, orgId]);

  const handleChangeOrgId = (event: SelectChangeEvent<string>) => {
    setOrgId(event.target.value);
  };

  const handleChangeProgramId = (event: SelectChangeEvent<string>) => {
    setProgramId(event.target.value);
  };

  const { mutateAsync, isPending } = useGenerateSubmissionsMutation(
    programId,
    orgId,
  );

  const handleGenerateSubmissions = async () => {
    try {
      await mutateAsync();

      toast.success('New submission generated!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    entries,
    totalCount: data.count,
    filters,
    setFilters,
    isLoading: isFetching,
    patients_ready_to_submit: data.patients_ready_to_submit,
    handleChangeOrgId,
    handleChangeProgramId,
    handleGenerateSubmissions,
    isGeneratePending: isPending,
  };
};
